import { supabase } from '../supabase';

export interface MentionNotification {
  id: string;
  recipient_id: string;
  sender_id: string;
  task_id: string;
  comment_id: string;
  message: string;
  read: boolean;
  created_at: string;
}

export class MentionNotificationService {
  
  /**
   * Crea notifiche per tutti gli utenti menzionati in un commento
   */
  static async createMentionNotifications(
    taskId: string,
    commentId: string,
    mentionedUserIds: string[],
    senderId: string,
    taskTitle: string,
    commentContent: string,
    projectId?: string
  ): Promise<void> {
    try {
      if (mentionedUserIds.length === 0) return;

      // Rimuove il sender dalle notifiche (non notificare se stesso)
      const recipientIds = mentionedUserIds.filter(id => id !== senderId);

      if (recipientIds.length === 0) return;

      // Get current user for organization_id and sender name
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('User not authenticated');
        return;
      }

      const organizationId = user.user_metadata?.organization_id;

      // Get sender name
      const { data: senderProfile } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('id', senderId)
        .single();

      const senderName = senderProfile?.full_name || 'Qualcuno';
      const commentPreview = commentContent
        ? commentContent.substring(0, 100) + (commentContent.length > 100 ? '...' : '')
        : '';

      // Se non abbiamo il projectId, lo recuperiamo dalla task
      let resolvedProjectId = projectId;
      if (!resolvedProjectId) {
        const { data: task } = await supabase
          .from('tasks')
          .select('project_id')
          .eq('id', taskId)
          .single();
        resolvedProjectId = task?.project_id;
      }

      const taskLink = resolvedProjectId
        ? `/projects/${resolvedProjectId}?task=${taskId}`
        : undefined;

      const title = 'Menzione in una task';
      const message = `${senderName} ti ha menzionato in un commento sulla task "${taskTitle}"${commentPreview ? `: ${commentPreview}` : ''}`;

      // Prepara le notifiche in-app
      const notifications = recipientIds.map(recipientId => ({
        user_id: recipientId,
        sender_id: senderId,
        sender_name: senderName,
        event_type: 'task_mention',
        type: 'info' as const,
        title,
        message,
        link: taskLink,
        entity_type: 'task',
        entity_id: taskId,
        priority: 'high',
        metadata: {
          task_id: taskId,
          comment_id: commentId,
          project_id: resolvedProjectId,
        },
        read: false,
        delivered_via: ['in_app'],
        organization_id: organizationId
      }));

      // INSERT diretto per in-app (UI reattiva)
      const { error } = await supabase
        .from('notifications')
        .insert(notifications);

      if (error) {
        console.error('❌ Error creating mention notifications:', error);
        throw error;
      }

      console.log(`✅ Created ${notifications.length} mention notification(s) for task "${taskTitle}"`);

      // Fire-and-forget: chiama create-notification per email/push
      if (organizationId) {
        supabase.functions.invoke('create-notification', {
          body: {
            organization_id: organizationId,
            event_type: 'task_mention',
            recipients: recipientIds,
            skip_in_app: true,
            data: {
              title,
              message,
              link: taskLink,
              entity_type: 'task',
              entity_id: taskId,
              sender_id: senderId,
              sender_name: senderName,
              priority: 'high',
              metadata: {
                task_id: taskId,
                comment_id: commentId,
                project_id: resolvedProjectId,
              }
            }
          }
        }).then(({ error: asyncError }) => {
          if (asyncError) {
            console.error('[MentionNotificationService] Async delivery error:', asyncError);
          }
        }).catch(err => {
          console.error('[MentionNotificationService] Async delivery failed:', err);
        });
      }

    } catch (error) {
      console.error('❌ Error in createMentionNotifications:', error);
      throw error;
    }
  }

  /**
   * Ottiene le notifiche non lette per un utente
   */
  static async getUnreadNotifications(userId: string): Promise<MentionNotification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          sender:user_profiles!notifications_sender_id_fkey (
            id,
            full_name,
            email,
            profile_image_url
          ),
          task:tasks (
            id,
            title
          )
        `)
        .eq('user_id', userId)
        .eq('read', false)
        .eq('type', 'mention')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
      return [];
    }
  }

  /**
   * Marca una notifica come letta
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Marca tutte le notifiche come lette per un utente
   */
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }
}